import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, FileSignature, Loader2, Sparkles, Copy, Download, PenLine } from "lucide-react";

const STATUS = {
  brouillon: { label: "Brouillon", cls: "bg-slate-100 text-slate-700" },
  en_cours: { label: "En cours", cls: "bg-blue-100 text-blue-700" },
  en_signature: { label: "En signature (partielle)", cls: "bg-amber-100 text-amber-700" },
  signee: { label: "Signée (3/3)", cls: "bg-emerald-100 text-emerald-700" },
  terminee: { label: "Terminée", cls: "bg-slate-200 text-slate-700" },
  archivee: { label: "Archivée", cls: "bg-amber-100 text-amber-700" },
};

const EMPTY = { titre: "", entreprise_id: "", salarie_id: "", objet: "", date_debut: "", date_fin: "", tarif_horaire: 0, statut: "brouillon" };

export default function Conventions() {
  const [items, setItems] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiDraft, setAiDraft] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const [a, b, c] = await Promise.all([api.get("/conventions"), api.get("/entreprises"), api.get("/salaries")]);
    setItems(a.data); setEntreprises(b.data); setSalaries(c.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, tarif_horaire: Number(form.tarif_horaire) || 0 };
      if (!payload.salarie_id) delete payload.salarie_id;
      await api.post("/conventions", payload);
      toast.success("Convention créée"); setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const generateAI = async () => {
    if (!form.entreprise_id || !form.objet || !form.date_debut || !form.date_fin) {
      toast.error("Renseignez entreprise, objet et dates.");
      return;
    }
    setAiLoading(true); setAiOpen(true); setAiDraft("");
    try {
      const { data } = await api.post("/assistant/generer-convention", {
        entreprise_id: form.entreprise_id,
        salarie_id: form.salarie_id || null,
        objet: form.objet,
        date_debut: form.date_debut,
        date_fin: form.date_fin,
        tarif_horaire: Number(form.tarif_horaire) || null,
      });
      setAiDraft(data.draft);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setAiOpen(false); }
    finally { setAiLoading(false); }
  };

  const entLabel = (id) => entreprises.find(e => e.id === id)?.raison_sociale || "—";
  const salLabel = (id) => { const s = salaries.find(x => x.id === id); return s ? `${s.prenom} ${s.nom}` : "—"; };

  const [signOpen, setSignOpen] = useState(false);
  const [signConv, setSignConv] = useState(null);
  const [signName, setSignName] = useState("");
  const [signQualite, setSignQualite] = useState("Représentant légal");
  const [signRole, setSignRole] = useState("entreprise"); // scic | entreprise | salarie

  const downloadPDF = async (id) => {
    try {
      const resp = await api.get(`/conventions/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const signer = async () => {
    if (!signName.trim()) { toast.error("Nom requis"); return; }
    try {
      const { data } = await api.post(`/conventions/${signConv.id}/signer`, { nom: signName, qualite: signQualite, role: signRole });
      if (data?.fully_signed) toast.success("Convention tripartite entièrement signée ✓");
      else toast.success(`Signature ${signRole} enregistrée (${(data?.signatures_roles || []).length}/3)`);
      setSignOpen(false); setSignName(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="conventions-page">
      <PageHeader
        title="Conventions de mise à disposition"
        subtitle="Création, suivi et signature des conventions L1253 entre le GE et les entreprises utilisatrices."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="convention-add-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                <Plus className="h-4 w-4 mr-2" /> Nouvelle convention
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Convention de mise à disposition</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2"><Label>Titre *</Label><Input required value={form.titre} onChange={(e)=>setForm({...form, titre: e.target.value})} data-testid="conv-titre" /></div>
                <div className="space-y-2">
                  <Label>Entreprise *</Label>
                  <Select value={form.entreprise_id} onValueChange={(v)=>setForm({...form, entreprise_id: v})}>
                    <SelectTrigger data-testid="conv-entreprise"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>{entreprises.map(e=><SelectItem key={e.id} value={e.id}>{e.raison_sociale}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Salarié</Label>
                  <Select value={form.salarie_id} onValueChange={(v)=>setForm({...form, salarie_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Optionnel" /></SelectTrigger>
                    <SelectContent>{salaries.map(s=><SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 col-span-2"><Label>Objet</Label><Textarea rows={2} value={form.objet} onChange={(e)=>setForm({...form, objet: e.target.value})} /></div>
                <div className="space-y-2"><Label>Date début *</Label><Input type="date" required value={form.date_debut} onChange={(e)=>setForm({...form, date_debut: e.target.value})} /></div>
                <div className="space-y-2"><Label>Date fin *</Label><Input type="date" required value={form.date_fin} onChange={(e)=>setForm({...form, date_fin: e.target.value})} /></div>
                <div className="space-y-2"><Label>Tarif horaire (€)</Label><Input type="number" step="0.01" value={form.tarif_horaire} onChange={(e)=>setForm({...form, tarif_horaire: e.target.value})} /></div>
                <div className="space-y-2">
                  <Label>Statut</Label>
                  <Select value={form.statut} onValueChange={(v)=>setForm({...form, statut: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS).map(([k,v])=><SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <DialogFooter className="col-span-2 flex flex-row justify-between">
                  <Button type="button" variant="outline" onClick={generateAI} data-testid="conv-ai-btn" className="border-[#C5A059] text-[#C5A059] hover:bg-[#FDF9F1]">
                    <Sparkles className="h-4 w-4 mr-2" /> Générer avec IA
                  </Button>
                  <Button type="submit" data-testid="conv-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Enregistrer</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Projet de convention généré par IA</DialogTitle></DialogHeader>
          {aiLoading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>
          ) : (
            <>
              <Textarea rows={18} value={aiDraft} onChange={(e)=>setAiDraft(e.target.value)} data-testid="conv-ai-draft" className="font-mono text-sm" />
              <DialogFooter>
                <Button variant="outline" onClick={()=>{ navigator.clipboard.writeText(aiDraft); toast.success("Copié"); }}>
                  <Copy className="h-4 w-4 mr-2" /> Copier
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <FileSignature className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">Aucune convention créée.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Titre</th><th className="px-4 py-3">Entreprise</th><th className="px-4 py-3">Salarié</th><th className="px-4 py-3">Période</th><th className="px-4 py-3 text-right">Tarif</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{c.titre}</td>
                  <td className="px-4 py-3 text-slate-600">{entLabel(c.entreprise_id)}</td>
                  <td className="px-4 py-3 text-slate-600">{salLabel(c.salarie_id)}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{c.date_debut} → {c.date_fin}</td>
                  <td className="px-4 py-3 text-right font-medium">{(c.tarif_horaire || 0).toFixed(2)} €/h</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded ${STATUS[c.statut]?.cls || "bg-slate-100 text-slate-700"}`}>{STATUS[c.statut]?.label || c.statut}</span>
                    <div className="text-[10px] text-slate-500 mt-1">
                      Signatures : {(c.signatures_roles || []).length}/3
                      {(c.signatures_roles || []).length > 0 && <> · {(c.signatures_roles || []).join(", ")}</>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={()=>downloadPDF(c.id)} data-testid={`conv-pdf-${c.id}`}><Download className="h-3 w-3 mr-1" />PDF</Button>
                      {c.statut !== "signee" && (
                        <Button size="sm" onClick={()=>{ setSignConv(c); setSignOpen(true); }} data-testid={`conv-sign-${c.id}`} className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                          <PenLine className="h-3 w-3 mr-1" />Signer
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Signature TRIPARTITE — Convention de mise à disposition</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-[#FDF9F1] border border-[#C5A059]/30 p-3 text-sm text-slate-700">
              Convention tripartite (SCIC employeur · Entreprise bénéficiaire · Salarié mis à disposition). Chaque partie doit signer son volet ; la convention est <strong>pleinement signée</strong> une fois les 3 signatures enregistrées.
              Signatures déjà enregistrées : <strong>{(signConv?.signatures_roles || []).join(", ") || "aucune"}</strong>
            </div>
            <div className="space-y-2">
              <Label>Partie signataire *</Label>
              <div className="flex gap-2">
                {["scic", "entreprise", "salarie"].map(r => {
                  const already = (signConv?.signatures_roles || []).includes(r);
                  return (
                    <button
                      key={r}
                      type="button"
                      disabled={already}
                      onClick={() => setSignRole(r)}
                      className={`flex-1 px-3 py-2 rounded-md text-sm border transition ${
                        signRole === r && !already ? "bg-[#C5A059] text-white border-[#C5A059]" :
                        already ? "bg-emerald-50 text-emerald-700 border-emerald-200 cursor-not-allowed" :
                        "bg-white border-slate-200 hover:border-slate-400"
                      }`}
                      data-testid={`sign-role-${r}`}
                    >
                      {r === "scic" ? "SCIC Employeur" : r === "entreprise" ? "Entreprise" : "Salarié"} {already && "✓"}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2"><Label>Nom complet du signataire *</Label><Input value={signName} onChange={(e)=>setSignName(e.target.value)} data-testid="conv-sign-name" placeholder="Jean DUPONT" /></div>
            <div className="space-y-2"><Label>Qualité</Label><Input value={signQualite} onChange={(e)=>setSignQualite(e.target.value)} data-testid="conv-sign-qualite" /></div>
            <DialogFooter>
              <Button onClick={signer} data-testid="conv-sign-confirm" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                <PenLine className="h-4 w-4 mr-2" />Signer en tant que {signRole}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
